import { ServicePriority, ServiceType } from "../enums/ServiceEnum";
import { Service, ServiceSequence } from "../models/ServiceModel";
import {
  isPlaceholderAddress,
  normalizeStreet,
  pickPreferredAddress,
  type BulkPassengerRow,
} from "../components/Passengers/bulkPassengerValidation";

export const PASSENGER_AUTO_SPLIT_THRESHOLD = 5;
export const MAX_PASSENGERS_PER_SERVICE = 6;

export type ParsedPassengerRow = {
  name: string;
  phone?: string | null;
  origin_address?: string | null;
  destination_address?: string | null;
};

export type ParsedRequestBlock = {
  direction?: "INGRESO" | "SALIDA" | "UNKNOWN" | string | null;
  service_date_text?: string | null;
  service_date?: string | null;
  service_time_text?: string | null;
  service_time?: string | null;
  schedule_note?: string | null;
  management?: string | null;
  superintendency?: string | null;
  area?: string | null;
  area_responsible?: string | null;
  area_responsible_phone?: string | null;
  cost_center_code?: string | null;
  service_reason?: string | null;
  shared_origin?: string | null;
  shared_destination?: string | null;
  passenger_count?: number | null;
  passengers: ParsedPassengerRow[];
};

export type ParsedElAbraExcel = {
  blocks: ParsedRequestBlock[];
  sheet_name?: string;
  notes?: string | null;
};

export type SelectOption = { value: string; label: string };

export type CatalogPassenger = {
  id: number;
  name: string;
  last_name?: string | null;
  first_lastname?: string | null;
  phone?: string | null;
  addresses?: { id: number; street: string }[];
};

export type ResolveContext = {
  companyId?: number | null;
  locations: SelectOption[];
  passengers: CatalogPassenger[];
  costCenters: SelectOption[];
  managements: SelectOption[];
  superintendencies: SelectOption[];
  areas: SelectOption[];
  authorizers: SelectOption[];
  requesters: SelectOption[];
};

export type ResolvedPassenger = {
  parsed: ParsedPassengerRow;
  passengerId: number | null;
  addressText: string;
  addressId: number | null;
  needsSelection: boolean;
};

export type ServiceDraftPlan = {
  key: string;
  label: string;
  blockIndex: number;
  vehicleIndex: number;
  direction?: string | null;
  service_date: string;
  service_time: string;
  service_type: ServiceType;
  description: string;
  originId: number | null;
  destinationId: number | null;
  originText: string | null;
  destinationText: string | null;
  costCenterId: number | null;
  cost_center_code?: string | null;
  managementText?: string | null;
  superintendencyText?: string | null;
  areaText?: string | null;
  managementId: number | null;
  superintendencyId: number | null;
  areaId: number | null;
  authorizerId: number | null;
  requesterId: number | null;
  serviceReasonText: string | null;
  domicileSharedOrigin?: string | null;
  domicileSharedDestination?: string | null;
  passengers: ResolvedPassenger[];
};

export type MissingField = {
  id: string;
  draftKey: string;
  label: string;
  kind:
    | "company"
    | "origin"
    | "destination"
    | "cost_center"
    | "management"
    | "superintendency"
    | "area"
    | "authorizer"
    | "requester"
    | "passenger"
    | "passenger_address";
  extractedText?: string;
  options: SelectOption[];
  value: string;
};

export type BlockDriverConfig = {
  blockIndex: number;
  driverCount: number;
  /** Distribución manual por móvil. Si existe, reemplaza el reparto parejo. */
  groups?: ParsedPassengerRow[][];
};

export function normalizeForMatch(value: string): string {
  return (value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const DOMICILIO_TEXT_MARKERS = [
  "domicilio",
  "domicilios",
  "domicilios pasajeros",
  "domicilio pasajero",
  "domicilios de pasajeros",
  "casa pasajero",
  "dir pasajero",
  "direccion pasajero",
];

export function isDomicilioPlaceholderText(text: string | null | undefined): boolean {
  const norm = normalizeForMatch(text || "");
  if (!norm) return false;
  return DOMICILIO_TEXT_MARKERS.some((marker) => norm.includes(normalizeForMatch(marker)));
}

function looksLikeStreetAddress(text: string | null | undefined): boolean {
  const value = (text || "").trim();
  if (value.length < 6) return false;
  return /\d/.test(value) && /[a-záéíóúñ]/i.test(value);
}

function passengerDomicileAddress(
  row: ParsedPassengerRow,
  block: ParsedRequestBlock,
  serviceType: ServiceType
): string | null {
  if (serviceType === ServiceType.ORIGEN_DOMICILIOS) {
    const rowOrigin = (row.origin_address || "").trim();
    if (rowOrigin && !isDomicilioPlaceholderText(rowOrigin)) return rowOrigin;
    const sharedOrigin = (block.shared_origin || "").trim();
    if (sharedOrigin && !isDomicilioPlaceholderText(sharedOrigin)) return sharedOrigin;
    return null;
  }
  if (serviceType === ServiceType.DESTINO_DOMICILIOS) {
    const rowDestination = (row.destination_address || "").trim();
    if (rowDestination && !isDomicilioPlaceholderText(rowDestination)) return rowDestination;
    const sharedDestination = (block.shared_destination || "").trim();
    if (sharedDestination && !isDomicilioPlaceholderText(sharedDestination)) return sharedDestination;
    return null;
  }
  return (row.origin_address || row.destination_address || "").trim() || null;
}

export function shouldResolveAsCatalogLocation(
  text: string | null | undefined,
  serviceType: ServiceType,
  side: "origin" | "destination"
): boolean {
  const value = (text || "").trim();
  if (!value || isDomicilioPlaceholderText(value)) return false;
  if (serviceType === ServiceType.ORIGEN_DOMICILIOS && side === "origin") return false;
  if (serviceType === ServiceType.DESTINO_DOMICILIOS && side === "destination") return false;
  if (
    serviceType === ServiceType.ORIGEN_DOMICILIOS &&
    side === "origin" &&
    looksLikeStreetAddress(value)
  ) {
    return false;
  }
  if (
    serviceType === ServiceType.DESTINO_DOMICILIOS &&
    side === "destination" &&
    looksLikeStreetAddress(value)
  ) {
    return false;
  }
  return true;
}

function fixedEndpointText(
  block: ParsedRequestBlock,
  serviceType: ServiceType,
  side: "origin" | "destination"
): string | null {
  const shared = side === "origin" ? block.shared_origin : block.shared_destination;
  const value = (shared || "").trim();
  if (!value || isDomicilioPlaceholderText(value)) return null;

  if (serviceType === ServiceType.ORIGEN_DOMICILIOS) {
    return side === "destination" ? value : null;
  }
  if (serviceType === ServiceType.DESTINO_DOMICILIOS) {
    return side === "origin" ? value : null;
  }
  return value;
}

export function tokenSimilarity(a: string, b: string): number {
  const ta = new Set(normalizeForMatch(a).split(" ").filter(Boolean));
  const tb = new Set(normalizeForMatch(b).split(" ").filter(Boolean));
  if (!ta.size || !tb.size) return 0;
  let inter = 0;
  ta.forEach((token) => {
    if (tb.has(token)) inter += 1;
  });
  return inter / Math.max(ta.size, tb.size);
}

export function fuzzyMatchOption<T extends SelectOption>(
  query: string | null | undefined,
  options: T[],
  minScore = 0.42
): { match: T | null; ambiguous: T[]; score: number } {
  const q = (query || "").trim();
  if (!q || !options.length) return { match: null, ambiguous: [], score: 0 };

  const scored = options
    .map((opt) => ({ opt, score: tokenSimilarity(q, opt.label) }))
    .filter((row) => row.score >= minScore)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return { match: null, ambiguous: [], score: 0 };

  const top = scored[0];
  const ties = scored.filter((row) => row.score >= top.score - 0.06);
  if (ties.length === 1) return { match: top.opt, ambiguous: [], score: top.score };
  return { match: null, ambiguous: ties.map((row) => row.opt), score: top.score };
}

export function minDriversForPassengers(passengerCount: number): number {
  return Math.max(1, Math.ceil(passengerCount / MAX_PASSENGERS_PER_SERVICE));
}

export function defaultDriversForPassengers(passengerCount: number): number {
  const min = minDriversForPassengers(passengerCount);
  if (passengerCount >= PASSENGER_AUTO_SPLIT_THRESHOLD) {
    return Math.max(min, 2);
  }
  return min;
}

export function splitPassengersEvenly<T>(rows: T[], driverCount: number): T[][] {
  const count = Math.max(1, Math.min(driverCount, rows.length || 1));
  const chunks: T[][] = Array.from({ length: count }, () => []);
  const base = Math.floor(rows.length / count);
  const remainder = rows.length % count;
  let idx = 0;
  for (let d = 0; d < count; d += 1) {
    const size = base + (d < remainder ? 1 : 0);
    chunks[d] = rows.slice(idx, idx + size);
    idx += size;
  }
  return chunks.filter((chunk) => chunk.length > 0);
}

/** Grupos del bloque: manuales si hay, o reparto parejo por defecto. */
export function resolveBlockPassengerGroups(
  passengers: ParsedPassengerRow[],
  driverCount: number,
  customGroups?: ParsedPassengerRow[][]
): ParsedPassengerRow[][] {
  if (customGroups && customGroups.length > 0) {
    return customGroups.filter((group) => group.length > 0);
  }
  return splitPassengersEvenly(passengers, driverCount);
}

/**
 * Mueve un pasajero entre móviles del mismo bloque.
 * - Máx. MAX_PASSENGERS_PER_SERVICE por móvil.
 * - Puede crear un móvil nuevo (toVehicle === groups.length) si hay cupo.
 * - Elimina móviles vacíos y renumera.
 */
export function movePassengerBetweenVehicles(
  groups: ParsedPassengerRow[][],
  fromVehicle: number,
  passengerIndex: number,
  toVehicle: number
): { groups: ParsedPassengerRow[][]; error?: string } {
  if (
    fromVehicle < 0 ||
    fromVehicle >= groups.length ||
    passengerIndex < 0 ||
    passengerIndex >= (groups[fromVehicle]?.length ?? 0)
  ) {
    return { groups, error: "Pasajero no encontrado en ese móvil" };
  }
  if (fromVehicle === toVehicle) {
    return { groups };
  }

  const creatingNew = toVehicle === groups.length;
  if (toVehicle < 0 || toVehicle > groups.length) {
    return { groups, error: "Móvil destino inválido" };
  }

  const sourceGroup = groups[fromVehicle];
  if (sourceGroup.length <= 1 && !creatingNew && groups.length === 1) {
    return { groups, error: "Debe quedar al menos un pasajero en el móvil" };
  }

  const passenger = sourceGroup[passengerIndex];
  const next = groups.map((group) => [...group]);

  next[fromVehicle] = next[fromVehicle].filter((_, idx) => idx !== passengerIndex);

  if (creatingNew) {
    next.push([passenger]);
  } else {
    if (next[toVehicle].length >= MAX_PASSENGERS_PER_SERVICE) {
      return {
        groups,
        error: `Máximo ${MAX_PASSENGERS_PER_SERVICE} pasajeros por móvil`,
      };
    }
    next[toVehicle] = [...next[toVehicle], passenger];
  }

  const cleaned = next.filter((group) => group.length > 0);
  for (const group of cleaned) {
    if (group.length > MAX_PASSENGERS_PER_SERVICE) {
      return {
        groups,
        error: `Máximo ${MAX_PASSENGERS_PER_SERVICE} pasajeros por móvil`,
      };
    }
  }

  return { groups: cleaned };
}

/**
 * Reasigna overrides de pasajero (`planKey:passenger:idx`) tras redistribuir móviles,
 * usando la identidad del nombre para no perder selecciones de catálogo.
 */
export function remapPassengerOverridesAfterRedistribution(
  overrides: Record<string, string>,
  oldPlans: ServiceDraftPlan[],
  newPlans: ServiceDraftPlan[]
): Record<string, string> {
  const byBlockIdentity = new Map<string, string>();

  oldPlans.forEach((plan) => {
    plan.passengers.forEach((p, idx) => {
      const key = `${plan.key}:passenger:${idx}`;
      const value = overrides[key];
      if (!value) return;
      const identity = planillaPassengerIdentityKey(p.parsed.name);
      byBlockIdentity.set(`${plan.blockIndex}|${identity}`, value);
    });
  });

  const next = { ...overrides };

  oldPlans.forEach((plan) => {
    plan.passengers.forEach((_, idx) => {
      delete next[`${plan.key}:passenger:${idx}`];
      delete next[`${plan.key}:passenger:${idx}:address`];
    });
  });

  newPlans.forEach((plan) => {
    plan.passengers.forEach((p, idx) => {
      const identity = planillaPassengerIdentityKey(p.parsed.name);
      const value = byBlockIdentity.get(`${plan.blockIndex}|${identity}`);
      if (value) {
        next[`${plan.key}:passenger:${idx}`] = value;
      }
    });
  });

  return next;
}

export function inferServiceType(block: ParsedRequestBlock): ServiceType {
  const direction = String(block.direction || "").toUpperCase();
  const sharedOrigin = (block.shared_origin || "").trim();
  const sharedDestination = (block.shared_destination || "").trim();
  const hasRowOrigins = block.passengers.some((p) => (p.origin_address || "").trim());
  const hasRowDestinations = block.passengers.some((p) => (p.destination_address || "").trim());

  const sharedOriginIsDomicilio = isDomicilioPlaceholderText(sharedOrigin);
  const sharedDestIsDomicilio = isDomicilioPlaceholderText(sharedDestination);
  const sharedOriginIsFixed =
    Boolean(sharedOrigin) && !sharedOriginIsDomicilio && !looksLikeStreetAddress(sharedOrigin);
  const sharedDestIsFixed =
    Boolean(sharedDestination) && !sharedDestIsDomicilio && !looksLikeStreetAddress(sharedDestination);

  if (direction === "INGRESO") return ServiceType.ORIGEN_DOMICILIOS;
  if (direction === "SALIDA") return ServiceType.DESTINO_DOMICILIOS;

  if (sharedDestIsDomicilio && sharedOriginIsFixed) return ServiceType.DESTINO_DOMICILIOS;
  if (sharedOriginIsDomicilio && sharedDestIsFixed) return ServiceType.ORIGEN_DOMICILIOS;

  if (sharedDestIsFixed && (hasRowOrigins || sharedOriginIsDomicilio || !sharedOrigin)) {
    return ServiceType.ORIGEN_DOMICILIOS;
  }
  if (sharedOriginIsFixed && (hasRowDestinations || sharedDestIsDomicilio || !sharedDestination)) {
    return ServiceType.DESTINO_DOMICILIOS;
  }

  if (sharedDestination && hasRowOrigins) return ServiceType.ORIGEN_DOMICILIOS;
  if (sharedOrigin && hasRowDestinations) return ServiceType.DESTINO_DOMICILIOS;

  return ServiceType.MANUAL;
}

function passengerLabel(p: CatalogPassenger): string {
  return `${p.name ?? ""} ${p.last_name ?? p.first_lastname ?? ""}`.trim();
}

function matchPassengerByName(
  fullName: string,
  catalog: CatalogPassenger[]
): CatalogPassenger | null {
  const normalized = normalizeForMatch(fullName);
  if (!normalized) return null;

  const scored = catalog
    .map((p) => ({
      p,
      score: tokenSimilarity(normalized, passengerLabel(p)),
    }))
    .filter((row) => row.score >= 0.55)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return null;
  if (scored.length > 1 && scored[0].score - scored[1].score < 0.08) return null;
  return scored[0].p;
}

function resolvePassengerRow(
  row: ParsedPassengerRow,
  block: ParsedRequestBlock,
  serviceType: ServiceType,
  catalog: CatalogPassenger[]
): ResolvedPassenger {
  const matched = matchPassengerByName(row.name, catalog);
  const addressHint = passengerDomicileAddress(row, block, serviceType);

  if (!matched) {
    return {
      parsed: row,
      passengerId: null,
      addressText: normalizeStreet(addressHint || ""),
      addressId: null,
      needsSelection: true,
    };
  }

  const picked = pickPreferredAddress(matched.addresses, addressHint || undefined);
  return {
    parsed: row,
    passengerId: matched.id,
    addressText: picked?.street || normalizeStreet(addressHint || ""),
    addressId: picked?.id ? picked.id : null,
    needsSelection: false,
  };
}

export function buildServicePlanLabel(
  block: ParsedRequestBlock,
  group: ParsedPassengerRow[],
  serviceType: ServiceType,
  options?: { blockIndex?: number; vehicleIndex?: number }
): string {
  const blockIndex = options?.blockIndex ?? 0;
  const vehicleIndex = options?.vehicleIndex ?? 0;

  const day = (block.service_date_text || block.service_date || "Sin fecha").trim();
  const hour = (block.service_time_text || block.service_time || "").trim();

  const dir = String(block.direction || "").toUpperCase();
  let serviceName = "";
  if (dir === "INGRESO") serviceName = "Ingreso";
  else if (dir === "SALIDA") serviceName = "Salida";
  else {
    const schedule = (block.schedule_note || "").toLowerCase();
    if (schedule.includes("ingreso")) serviceName = "Ingreso";
    else if (schedule.includes("salida")) serviceName = "Salida";
    else if (block.schedule_note?.trim()) {
      serviceName = block.schedule_note.trim().split(/\r?\n/)[0].slice(0, 48);
    } else {
      serviceName = `Tramo ${blockIndex + 1}`;
    }
  }

  const originFixed = fixedEndpointText(block, serviceType, "origin");
  const destFixed = fixedEndpointText(block, serviceType, "destination");
  const originDisplay =
    serviceType === ServiceType.ORIGEN_DOMICILIOS
      ? "Domicilios pasajeros"
      : originFixed || (block.shared_origin || "").trim() || "—";
  const destDisplay =
    serviceType === ServiceType.DESTINO_DOMICILIOS
      ? "Domicilios pasajeros"
      : destFixed || (block.shared_destination || "").trim() || "—";

  const paxCount = group.length;
  const names = group.map((p) => p.name.trim()).filter(Boolean);
  const passengerList = names.length ? names.join(", ") : "—";

  const parts = [
    day,
    hour ? `Hora ${hour}` : "",
    serviceName,
    `Origen: ${originDisplay}`,
    `Destino: ${destDisplay}`,
    `${paxCount} pax`,
    passengerList,
  ].filter(Boolean);

  const mobileNote = vehicleIndex > 0 ? ` · Móvil ${vehicleIndex + 1}` : "";
  return parts.join(" · ") + mobileNote;
}

export function buildDefaultBlockDriverConfigs(parsed: ParsedElAbraExcel): BlockDriverConfig[] {
  return parsed.blocks.map((block, blockIndex) => ({
    blockIndex,
    driverCount: defaultDriversForPassengers(block.passengers?.length || 0),
  }));
}

export function buildServiceDraftPlans(
  parsed: ParsedElAbraExcel,
  blockDrivers: BlockDriverConfig[]
): ServiceDraftPlan[] {
  const plans: ServiceDraftPlan[] = [];

  parsed.blocks.forEach((block, blockIndex) => {
    const passengers = Array.isArray(block.passengers) ? block.passengers : [];
    const config = blockDrivers.find((row) => row.blockIndex === blockIndex);
    const driverCount = config?.driverCount ?? defaultDriversForPassengers(passengers.length);
    const groups = resolveBlockPassengerGroups(passengers, driverCount, config?.groups);

    groups.forEach((group, vehicleIndex) => {
      const serviceType = inferServiceType(block);
      const originText = fixedEndpointText(block, serviceType, "origin");
      const destinationText = fixedEndpointText(block, serviceType, "destination");

      plans.push({
        key: `b${blockIndex}-v${vehicleIndex}`,
        label: buildServicePlanLabel(block, group, serviceType, { blockIndex, vehicleIndex }),
        blockIndex,
        vehicleIndex,
        direction: block.direction,
        service_date: block.service_date || "",
        service_time: block.service_time || "",
        service_type: serviceType,
        description: [block.schedule_note, block.service_reason].filter(Boolean).join(" — "),
        originId: null,
        destinationId: null,
        originText: originText || null,
        destinationText: destinationText || null,
        costCenterId: null,
        cost_center_code: block.cost_center_code || null,
        managementText: block.management || null,
        superintendencyText: block.superintendency || null,
        areaText: block.area || null,
        managementId: null,
        superintendencyId: null,
        areaId: null,
        authorizerId: null,
        requesterId: null,
        serviceReasonText: block.service_reason || null,
        domicileSharedOrigin: block.shared_origin || null,
        domicileSharedDestination: block.shared_destination || null,
        passengers: group.map((row) => resolvePassengerRow(row, block, serviceType, [])),
      });
    });
  });

  return plans;
}

export function resolveDraftPlans(
  plans: ServiceDraftPlan[],
  context: ResolveContext,
  overrides: Record<string, string> = {}
): { plans: ServiceDraftPlan[]; missing: MissingField[] } {
  const missing: MissingField[] = [];
  const resolved = plans.map((plan) => {
    const domicileBlock: ParsedRequestBlock = {
      shared_origin: plan.domicileSharedOrigin,
      shared_destination: plan.domicileSharedDestination,
      passengers: [],
    };

    const next: ServiceDraftPlan = {
      ...plan,
      passengers: plan.passengers.map((p) => {
        const matched = matchPassengerByName(p.parsed.name, context.passengers);
        const addressHint = passengerDomicileAddress(
          p.parsed,
          domicileBlock,
          plan.service_type
        );

        if (!matched) return { ...p, needsSelection: true };

        const picked = pickPreferredAddress(matched.addresses, addressHint || undefined);
        return {
          ...p,
          passengerId: matched.id,
          addressText: picked?.street || normalizeStreet(addressHint || ""),
          addressId: picked?.id ? picked.id : null,
          needsSelection: false,
        };
      }),
    };

    const originOverride = overrides[`${plan.key}:origin`];
    const destinationOverride = overrides[`${plan.key}:destination`];
    const costCenterOverride =
      overrides[`${plan.key}:cost_center`] ||
      overrides[`block:${plan.blockIndex}:cost_center`] ||
      overrides.cost_center;

    const originMatch = originOverride
      ? { match: { value: originOverride, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.originText, context.locations);
    const destinationMatch = destinationOverride
      ? { match: { value: destinationOverride, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.destinationText, context.locations);
    const costCenterMatch = costCenterOverride
      ? { match: { value: costCenterOverride, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.cost_center_code, context.costCenters, 0.35);

    const managementMatch = overrides.management
      ? { match: { value: overrides.management, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.managementText, context.managements);
    const superintendencyMatch = overrides.superintendency
      ? { match: { value: overrides.superintendency, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.superintendencyText, context.superintendencies);
    const areaMatch = overrides.area
      ? { match: { value: overrides.area, label: "" }, ambiguous: [] as SelectOption[], score: 1 }
      : fuzzyMatchOption(plan.areaText, context.areas);

    if (managementMatch.match) next.managementId = Number(managementMatch.match.value);
    if (superintendencyMatch.match) next.superintendencyId = Number(superintendencyMatch.match.value);
    if (areaMatch.match) next.areaId = Number(areaMatch.match.value);

    if (overrides.authorizer) next.authorizerId = Number(overrides.authorizer);
    if (overrides.requester) next.requesterId = Number(overrides.requester);

    if (
      shouldResolveAsCatalogLocation(plan.originText, plan.service_type, "origin") &&
      plan.originText
    ) {
      if (originMatch.match) next.originId = Number(originMatch.match.value);
      else {
        missing.push({
          id: `${plan.key}:origin`,
          draftKey: plan.key,
          label: `Origen · ${plan.label}`,
          kind: "origin",
          extractedText: plan.originText,
          options: originMatch.ambiguous.length ? originMatch.ambiguous : context.locations,
          value: originOverride || "",
        });
      }
    }

    if (
      shouldResolveAsCatalogLocation(plan.destinationText, plan.service_type, "destination") &&
      plan.destinationText
    ) {
      if (destinationMatch.match) next.destinationId = Number(destinationMatch.match.value);
      else {
        missing.push({
          id: `${plan.key}:destination`,
          draftKey: plan.key,
          label: `Destino · ${plan.label}`,
          kind: "destination",
          extractedText: plan.destinationText,
          options: destinationMatch.ambiguous.length ? destinationMatch.ambiguous : context.locations,
          value: destinationOverride || "",
        });
      }
    }

    if (plan.cost_center_code) {
      if (costCenterMatch.match) next.costCenterId = Number(costCenterMatch.match.value);
      else {
        missing.push({
          id: `${plan.key}:cost_center`,
          draftKey: plan.key,
          label: `${plan.label}: centro de costo`,
          kind: "cost_center",
          extractedText: plan.cost_center_code || "",
          options: costCenterMatch.ambiguous.length ? costCenterMatch.ambiguous : context.costCenters,
          value: costCenterOverride || "",
        });
      }
    }

    next.passengers.forEach((p, idx) => {
      const overrideKey = `${plan.key}:passenger:${idx}`;
      const overrideVal = overrides[overrideKey];
      if (overrideVal) {
        p.passengerId = Number(overrideVal);
        p.needsSelection = false;
        const catalogPax = context.passengers.find((row) => row.id === Number(overrideVal));
        if (catalogPax) {
          const picked = pickPreferredAddress(catalogPax.addresses, p.addressText || undefined);
          if (picked) {
            p.addressText = picked.street;
            p.addressId = picked.id || null;
          }
        }
      }
      if (!p.passengerId) {
        missing.push({
          id: overrideKey,
          draftKey: plan.key,
          label: `${plan.label}: pasajero «${p.parsed.name}»`,
          kind: "passenger",
          extractedText: p.parsed.name,
          options: context.passengers.map((row) => ({
            value: String(row.id),
            label: passengerLabel(row),
          })),
          value: overrideVal || "",
        });
      } else if (
        (plan.service_type === ServiceType.ORIGEN_DOMICILIOS ||
          plan.service_type === ServiceType.DESTINO_DOMICILIOS) &&
        (!p.addressText || isPlaceholderAddress(p.addressText))
      ) {
        const domicileHint = passengerDomicileAddress(
          p.parsed,
          domicileBlock,
          plan.service_type
        );
        missing.push({
          id: `${overrideKey}:address`,
          draftKey: plan.key,
          label: `${plan.label}: domicilio de «${p.parsed.name}»`,
          kind: "passenger_address",
          extractedText: domicileHint || p.addressText || "",
          options: [],
          value: "",
        });
      }
    });

    return next;
  });

  if (!context.companyId) {
    missing.unshift({
      id: "company",
      draftKey: "",
      label: "Empresa",
      kind: "company",
      options: [],
      value: overrides.company || "",
    });
  }

  const requiredPersonFields: MissingField[] = [];
  if (!overrides.authorizer) {
    requiredPersonFields.push({
      id: "authorizer",
      draftKey: "",
      label: "Autorizador",
      kind: "authorizer",
      options: context.authorizers,
      value: "",
    });
  }
  if (!overrides.requester) {
    requiredPersonFields.push({
      id: "requester",
      draftKey: "",
      label: "Solicitante",
      kind: "requester",
      options: context.requesters,
      value: "",
    });
  }
  if (requiredPersonFields.length) {
    const companyIdx = missing.findIndex((m) => m.id === "company");
    const insertAt = companyIdx >= 0 ? companyIdx + 1 : 0;
    missing.splice(insertAt, 0, ...requiredPersonFields);
  }

  const sample = resolved[0];
  if (sample) {
    const catalogChecks = [
      {
        id: "management",
        kind: "management" as const,
        label: "Gerencia",
        text: sample.managementText,
        options: context.managements,
        resolved: sample.managementId,
      },
      {
        id: "superintendency",
        kind: "superintendency" as const,
        label: "Superintendencia",
        text: sample.superintendencyText,
        options: context.superintendencies,
        resolved: sample.superintendencyId,
      },
      {
        id: "area",
        kind: "area" as const,
        label: "Área",
        text: sample.areaText,
        options: context.areas,
        resolved: sample.areaId,
      },
    ];

    for (const row of catalogChecks) {
      if (!row.text?.trim() || overrides[row.id] || row.resolved) continue;
      const match = fuzzyMatchOption(row.text, row.options);
      if (!match.match && !missing.some((m) => m.id === row.id)) {
        missing.push({
          id: row.id,
          draftKey: "",
          label: row.label,
          kind: row.kind,
          extractedText: row.text,
          options: match.ambiguous.length ? match.ambiguous : row.options,
          value: "",
        });
      }
    }
  }

  return { plans: resolved, missing };
}

export function plansAreReady(
  plans: ServiceDraftPlan[],
  companyId?: number | null
): boolean {
  if (!companyId) return false;
  return plans.every((plan) => {
    const originOk =
      plan.service_type === ServiceType.ORIGEN_DOMICILIOS || Boolean(plan.originId);
    const destinationOk =
      plan.service_type === ServiceType.DESTINO_DOMICILIOS || Boolean(plan.destinationId);
    const passengersOk =
      plan.passengers.length > 0 &&
      plan.passengers.length <= MAX_PASSENGERS_PER_SERVICE &&
      plan.passengers.every((p) => p.passengerId);
    const domicileAddressesOk =
      plan.service_type === ServiceType.MANUAL
        ? true
        : plan.passengers.every(
            (p) => Boolean(p.addressText) && !isPlaceholderAddress(p.addressText)
          );
    return (
      Boolean(plan.service_date) &&
      Boolean(plan.service_time) &&
      Boolean(plan.authorizerId) &&
      Boolean(plan.requesterId) &&
      originOk &&
      destinationOk &&
      passengersOk &&
      domicileAddressesOk
    );
  });
}

export function draftPlanToServicePayload(
  plan: ServiceDraftPlan,
  companyId: number
): Service {
  const sequences: ServiceSequence[] = plan.passengers.map((p, index) => ({
    passenger: p.passengerId!,
    address: p.addressText,
    ...(p.addressId != null ? { address_id: p.addressId } : {}),
    order: index + 1,
  }));

  return {
    company: companyId,
    service_type: plan.service_type,
    priority: ServicePriority.NORMAL,
    service_date: plan.service_date,
    service_time: plan.service_time,
    description: plan.description,
    origin: plan.service_type === ServiceType.ORIGEN_DOMICILIOS ? null : plan.originId,
    destination:
      plan.service_type === ServiceType.DESTINO_DOMICILIOS ? null : plan.destinationId,
    cost_center: plan.costCenterId,
    authorizer: plan.authorizerId,
    requester: plan.requesterId,
    sequences,
  };
}

export function resolveCostCenterOverride(
  overrides: Record<string, string>,
  plan: ServiceDraftPlan
): string {
  return (
    overrides[`${plan.key}:cost_center`] ||
    overrides[`block:${plan.blockIndex}:cost_center`] ||
    overrides.cost_center ||
    ""
  );
}

export function buildPlanColumnHeader(plan: ServiceDraftPlan): {
  title: string;
  subtitle: string;
} {
  const clean = plan.label.replace(/ · Móvil \d+$/, "");
  const parts = clean.split(" · ");
  const title = [parts[0], parts[1], parts[2]].filter(Boolean).join(" · ");
  const route = [parts[3], parts[4]].filter(Boolean).join(" · ");
  const pax = parts[5] || `${plan.passengers.length} pax`;
  const names = parts[6] || plan.passengers.map((p) => p.parsed.name).join(", ");
  return {
    title: title || clean,
    subtitle: [route, pax, names].filter(Boolean).join(" · "),
  };
}

export function planDateLabel(plan: ServiceDraftPlan): string {
  return plan.label.split(" · ")[0]?.trim() || plan.service_date || "—";
}

export function planDirectionLabel(plan: ServiceDraftPlan): string {
  const dir = String(plan.direction || "").toUpperCase();
  if (dir === "INGRESO") return "Ingreso";
  if (dir === "SALIDA") return "Salida";
  const fromLabel = plan.label.split(" · ")[2]?.trim();
  if (!fromLabel) return "—";
  const lower = fromLabel.toLowerCase();
  if (lower.includes("ingreso")) return "Ingreso";
  if (lower.includes("salida")) return "Salida";
  return fromLabel;
}

export function planillaNameToBulkRow(
  fullName: string,
  street?: string | null,
  phone?: string | null
): BulkPassengerRow {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { name: "", first_lastname: "", second_lastname: "", phone: phone || "", street: street || "" };
  }
  if (parts.length === 1) {
    return {
      name: parts[0],
      first_lastname: "",
      second_lastname: "",
      phone: (phone || "").trim(),
      street: (street || "").trim(),
    };
  }
  return {
    name: parts[0],
    first_lastname: parts[1],
    second_lastname: parts.slice(2).join(" "),
    phone: (phone || "").trim(),
    street: (street || "").trim(),
  };
}

export function planillaPassengerToBulkRow(
  plan: ServiceDraftPlan,
  passenger: ResolvedPassenger
): BulkPassengerRow {
  const block: ParsedRequestBlock = {
    shared_origin: plan.domicileSharedOrigin,
    shared_destination: plan.domicileSharedDestination,
    passengers: [],
  };
  const street =
    passengerDomicileAddress(passenger.parsed, block, plan.service_type) ||
    passenger.addressText ||
    "";
  return planillaNameToBulkRow(passenger.parsed.name, street, passenger.parsed.phone);
}

export type PlanillaUnmatchedPassenger = {
  overrideKey: string;
  planKey: string;
  planLabel: string;
  displayName: string;
  plan: ServiceDraftPlan;
  passenger: ResolvedPassenger;
  passengerIndex: number;
};

export function collectUnmatchedPassengerSlots(
  plans: ServiceDraftPlan[],
  overrides: Record<string, string> = {}
): PlanillaUnmatchedPassenger[] {
  const slots: PlanillaUnmatchedPassenger[] = [];
  plans.forEach((plan) => {
    plan.passengers.forEach((passenger, passengerIndex) => {
      const overrideKey = `${plan.key}:passenger:${passengerIndex}`;
      if (passenger.passengerId || overrides[overrideKey]) return;
      slots.push({
        overrideKey,
        planKey: plan.key,
        planLabel: plan.label,
        displayName: passenger.parsed.name,
        plan,
        passenger,
        passengerIndex,
      });
    });
  });
  return slots;
}

export function planillaPassengerIdentityKey(fullName: string): string {
  return normalizeForMatch(fullName);
}

export function applyPassengerIdToPlanillaDuplicates(
  plans: ServiceDraftPlan[],
  displayName: string,
  passengerId: string,
  overrides: Record<string, string> = {}
): Record<string, string> {
  const identity = planillaPassengerIdentityKey(displayName);
  const next = { ...overrides };
  plans.forEach((plan) => {
    plan.passengers.forEach((passenger, passengerIndex) => {
      if (passenger.passengerId) return;
      const overrideKey = `${plan.key}:passenger:${passengerIndex}`;
      if (planillaPassengerIdentityKey(passenger.parsed.name) !== identity) return;
      next[overrideKey] = passengerId;
    });
  });
  return next;
}

export function dedupeUnmatchedSlotsForBulk(
  slots: PlanillaUnmatchedPassenger[]
): PlanillaUnmatchedPassenger[] {
  const seen = new Set<string>();
  const result: PlanillaUnmatchedPassenger[] = [];
  slots.forEach((slot) => {
    const identity = planillaPassengerIdentityKey(slot.displayName);
    if (seen.has(identity)) return;
    seen.add(identity);
    result.push(slot);
  });
  return result;
}

export function anyPlanNeedsCostCenter(plans: ServiceDraftPlan[]): boolean {
  return plans.some((plan) => Boolean(plan.cost_center_code));
}

export function serviceTypeLabel(serviceType: ServiceType): string {
  switch (serviceType) {
    case ServiceType.ORIGEN_DOMICILIOS:
      return "Origen en domicilios (recogida en casas → destino fijo)";
    case ServiceType.DESTINO_DOMICILIOS:
      return "Destino en domicilios (origen fijo → entrega en casas)";
    default:
      return "Manual (origen y destino fijos)";
  }
}

export function serviceTypeShortLabel(serviceType: ServiceType): string {
  switch (serviceType) {
    case ServiceType.ORIGEN_DOMICILIOS:
      return "Domicilios → fijo";
    case ServiceType.DESTINO_DOMICILIOS:
      return "Fijo → domicilios";
    default:
      return "Manual";
  }
}

export function countTotalPassengers(parsed: ParsedElAbraExcel): number {
  return parsed.blocks.reduce((acc, block) => acc + (block.passengers?.length || 0), 0);
}
